import { FC, useMemo, useState } from 'react';

import { AdditionalOption } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { formatPrice } from '@shared/utils/formatPrice';

interface GiftAdditionalOptionsProps {
  options: AdditionalOption<Image>[];
  /** optionName -> 슬롯별 선택한 choiceName (index = slotIndex) */
  selected: Record<string, string[]>;
  onSelect: (optionName: string, slotIndex: number, choiceName: string) => void;
  /** 루트 className (여백 조절용). 기본 'mt-5 space-y-3' */
  className?: string;
  /** true면 옵션명/슬롯 라벨 헤더를 숨기고 셀렉터만 렌더 (라벨이 이미 다른 곳에 노출될 때) */
  hideLabels?: boolean;
  /** 강조할 옵션명 (필수 미선택 안내 시 ring 표시) */
  highlightedName?: string | null;
  /** 안내(옵션 상세)가 있는 옵션이면 true → '옵션 보러가기' 버튼 노출 */
  hasInfo?: (optionName: string) => boolean;
  /** '옵션 보러가기' 클릭 시 해당 옵션 안내로 스크롤 */
  onViewInfo?: (optionName: string) => void;
}

const sortByOrder = <T extends { order?: number }>(arr: T[]): T[] =>
  [...arr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const priceLabel = (priceDelta: number): string =>
  priceDelta > 0 ? `+${formatPrice(priceDelta)}원` : '무료';

/**
 * 쇼핑몰식 옵션 셀렉트 (선택 전용 커스텀 드롭다운).
 * selectionCount가 2 이상이면 같은 choices를 공유하는 셀렉터를 슬롯 수만큼 렌더한다.
 */
export const GiftAdditionalOptions: FC<GiftAdditionalOptionsProps> =
  function GiftAdditionalOptions({
    options,
    selected,
    onSelect,
    className,
    hideLabels,
    highlightedName,
    hasInfo,
    onViewInfo,
  }) {
    const sortedOptions = useMemo(() => sortByOrder(options), [options]);
    const [openKey, setOpenKey] = useState<string | null>(null);

    if (sortedOptions.length === 0) return null;

    return (
      <div className={className ?? 'mt-5 space-y-3'}>
        {sortedOptions.map((option) => {
          const choices = sortByOrder(
            (option.choices ?? []).filter((c) => c.enabled !== false),
          );
          if (choices.length === 0) return null;

          const count = Math.max(1, option.selectionCount ?? 1);
          const slotLabel = (i: number) =>
            option.slotLabels?.[i]?.trim() || `${option.name}${i + 1}`;

          return (
            <div
              key={option.name}
              data-gift-option={option.name}
              className={
                highlightedName === option.name
                  ? 'scroll-mt-20 rounded-lg p-2 ring-2 ring-red-400 transition-shadow'
                  : undefined
              }
            >
              {!hideLabels && (
                <div className="mb-1 flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-gray-800">
                    {option.name}
                  </span>
                  {option.required && (
                    <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600">
                      필수
                    </span>
                  )}
                  {hasInfo?.(option.name) && (
                    <button
                      type="button"
                      onClick={() => onViewInfo?.(option.name)}
                      className="ml-auto shrink-0 text-xs font-medium text-indigo-600 underline-offset-2 active:underline"
                    >
                      옵션 보러가기 ›
                    </button>
                  )}
                </div>
              )}

              <div className="space-y-2">
                {Array.from({ length: count }).map((_, slot) => {
                  const key = `${option.name}#${slot}`;
                  const selectedName = selected[option.name]?.[slot];
                  const selectedChoice =
                    choices.find((c) => c.name === selectedName) ?? null;
                  const isOpen = openKey === key;

                  return (
                    <div key={slot}>
                      {count > 1 && (
                        <p className="mb-0.5 text-xs font-medium text-gray-500">
                          {slotLabel(slot)}
                        </p>
                      )}

                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setOpenKey(isOpen ? null : key)}
                          className={`flex w-full items-center justify-between rounded-lg border bg-white px-3 py-2.5 text-sm ${
                            isOpen ? 'border-gray-800' : 'border-gray-300'
                          }`}
                        >
                          {selectedChoice ? (
                            <span className="flex items-center gap-2">
                              <span
                                className="font-bold"
                                style={{ color: selectedChoice.colorHex || undefined }}
                              >
                                {selectedChoice.name}
                              </span>
                              <span
                                className={`text-xs ${
                                  selectedChoice.priceDelta > 0
                                    ? 'font-semibold text-gray-700'
                                    : 'text-gray-400'
                                }`}
                              >
                                {priceLabel(selectedChoice.priceDelta)}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400">선택하세요</span>
                          )}
                          <span
                            className={`ml-2 shrink-0 text-gray-400 transition-transform ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          >
                            ▾
                          </span>
                        </button>

                        {isOpen && (
                          <>
                            {/* 바깥 클릭 닫기 */}
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenKey(null)}
                            />
                            <div className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                              {choices.map((choice) => {
                                const isSel = choice.name === selectedName;
                                return (
                                  <button
                                    key={choice.name}
                                    type="button"
                                    onClick={() => {
                                      onSelect(option.name, slot, choice.name);
                                      setOpenKey(null);
                                    }}
                                    className={`flex w-full items-center justify-between px-3 py-2.5 text-left ${
                                      isSel
                                        ? 'bg-indigo-50/60'
                                        : 'bg-white hover:bg-gray-50'
                                    }`}
                                  >
                                    <span
                                      className="font-bold"
                                      style={{ color: choice.colorHex || undefined }}
                                    >
                                      {choice.name}
                                    </span>
                                    <span
                                      className={`ml-2 shrink-0 text-xs ${
                                        choice.priceDelta > 0
                                          ? 'font-semibold text-gray-700'
                                          : 'text-gray-400'
                                      }`}
                                    >
                                      {priceLabel(choice.priceDelta)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };
